import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { progressService } from '@/services/progress.service';
import { streamService } from '@/services/stream.service';
import { courseKeys } from './useCourses';
import { enrollmentKeys } from './useEnrollments';

export const progressKeys = {
  all: ['progress'] as const,
  course: (courseId: string) =>
    [...progressKeys.all, 'course', courseId] as const,
  lesson: (lessonId: string) =>
    [...progressKeys.all, 'lesson', lessonId] as const,
  history: () => [...progressKeys.all, 'history'] as const,
  continue: () => [...progressKeys.all, 'continue'] as const,
  nextLesson: (courseId: string) =>
    [...progressKeys.all, 'next', courseId] as const,
};

// Queries
export function useCourseProgress(courseId: string, enabled = true) {
  return useQuery({
    queryKey: progressKeys.course(courseId),
    queryFn: () => progressService.getCourseProgress(courseId),
    enabled: enabled && !!courseId,
  });
}

export function useLessonProgress(lessonId: string, enabled = true) {
  return useQuery({
    queryKey: progressKeys.lesson(lessonId),
    queryFn: () => progressService.getLessonProgress(lessonId),
    enabled: enabled && !!lessonId,
  });
}

export function useContinueLesson() {
  return useQuery({
    queryKey: progressKeys.continue(),
    queryFn: () => progressService.getContinueLesson(),
  });
}

export function useWatchHistory(limit = 10) {
  return useQuery({
    queryKey: [...progressKeys.history(), limit],
    queryFn: () => progressService.getWatchHistory(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useNextLesson(courseId: string, enabled = true) {
  return useQuery({
    queryKey: progressKeys.nextLesson(courseId),
    queryFn: () => progressService.getNextLesson(courseId),
    enabled: enabled && !!courseId,
  });
}

// Mutations
export function useUpdateLessonProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lessonId,
      watchedSeconds,
      totalSeconds,
    }: {
      lessonId: string;
      watchedSeconds: number;
      totalSeconds?: number;
    }) =>
      progressService.updateLessonProgress(lessonId, {
        watchedSeconds,
        totalSeconds,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(progressKeys.lesson(variables.lessonId), data);
    },
  });
}

export function useSyncStreamLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { lessonId: string; courseId: string }) =>
      streamService.lessonSync(variables.lessonId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(progressKeys.lesson(variables.lessonId), data);
      queryClient.invalidateQueries({
        queryKey: progressKeys.course(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: progressKeys.nextLesson(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.modules(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.lesson(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.my() });
    },
  });
}

export function useMarkLessonComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { lessonId: string; courseId: string }) =>
      progressService.markLessonComplete(variables.lessonId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(progressKeys.lesson(variables.lessonId), data);
      queryClient.invalidateQueries({
        queryKey: progressKeys.course(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: progressKeys.nextLesson(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.modules(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.lesson(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.my() });
    },
  });
}

export function useMarkLessonIncomplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { lessonId: string; courseId: string }) =>
      progressService.markLessonIncomplete(variables.lessonId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(progressKeys.lesson(variables.lessonId), data);
      queryClient.invalidateQueries({
        queryKey: progressKeys.course(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: progressKeys.nextLesson(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.modules(variables.courseId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.lesson(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.my() });
    },
  });
}

export function useRecordWatchTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { lessonId: string; duration: number }) =>
      progressService.recordWatchTime(variables.lessonId, variables.duration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: progressKeys.history() });
    },
  });
}
